<template>
    <div class="gap-sm flex flex-col">
        <h1>Organization Onboarding Form</h1>
        <p>
            Please fill out this form with the required company and billing information. This will serve as the primary
            point of contact for your organization and ensure a smooth integration into the system.
        </p>
    </div>

    <form class="gap-y-md flex flex-col">
        <h2 class="text-md">Company info</h2>
        <div class="input-group">
            <label for="vat-no" class="required">Vat no.</label>
            <div class="input-wrapper">
                <input id="vat-no" v-model="vatNo" type="text" placeholder="AA000000000" />
            </div>
        </div>
        <div class="gap-sm grid grid-cols-4">
            <div class="input-group">
                <label for="zip-code" class="required">Zip code</label>
                <div class="input-wrapper">
                    <input id="zip-code" v-model="zip" type="text" placeholder="00000" />
                </div>
            </div>
            <div class="input-group col-span-3">
                <label for="city" class="required">City</label>
                <div class="input-wrapper">
                    <input id="city" v-model="city" type="text" placeholder="Select city" />
                </div>
            </div>
        </div>
        <div class="input-group">
            <label for="address" class="required">Address (street)</label>
            <div class="input-wrapper">
                <input id="address" v-model="address" type="text" placeholder="Enter street address" />
            </div>
        </div>
        <div class="gap-sm grid grid-cols-2">
            <div class="input-group">
                <label for="email" class="required">Email address</label>
                <div class="input-wrapper">
                    <input id="email" v-model="email" type="text" placeholder="example@atnorth.com" />
                </div>
            </div>
            <div class="input-group">
                <label for="phone">Phone number</label>
                <div class="input-wrapper">
                    <IntlTelInput
                        :options="{ initialCountry: 'is' }"
                        @change-number="setPhoneNumber"
                        @change-validity="setValidPhoneNumber"
                    />
                </div>
            </div>
        </div>
        <div class="input-group">
            <label for="org-website" class="required">Organization website</label>
            <div class="input-wrapper">
                <input id="org-website" v-model="website" type="text" placeholder="www.example.com" />
            </div>
        </div>
    </form>

    <form class="gap-y-md flex flex-col">
        <h2 class="text-md">Billing info</h2>
        <div class="input-group">
            <label for="billing-contact" class="required">Billing contact</label>
            <div class="input-wrapper">
                <input
                    id="billing-contact"
                    v-model="billingContact"
                    type="text"
                    placeholder="Enter contact full name"
                />
            </div>
        </div>
        <div class="input-group">
            <label for="billing-department" class="required">Department</label>
            <div class="input-wrapper">
                <input id="billing-department" v-model="billingDepartment" type="text" placeholder="Enter department" />
            </div>
        </div>
        <div class="gap-sm grid grid-cols-4">
            <div class="input-group">
                <label for="billing-zip-code" class="required">Zip code</label>
                <div class="input-wrapper">
                    <input id="billing-zip-code" v-model="billingZip" type="text" placeholder="00000" />
                </div>
            </div>
            <div class="input-group col-span-3">
                <label for="billing-city" class="required">City</label>
                <div class="input-wrapper">
                    <input id="billing-city" v-model="billingCity" type="text" placeholder="Select city" />
                </div>
            </div>
        </div>
        <div class="input-group">
            <label for="billing-address" class="required">Billing address, Street</label>
            <div class="input-wrapper">
                <input id="billing-address" v-model="billingAddress" type="text" placeholder="Enter street address" />
            </div>
        </div>
        <div class="gap-sm grid grid-cols-2">
            <div class="input-group">
                <label for="billing-email" class="required">Email address</label>
                <div class="input-wrapper">
                    <input id="billing-email" v-model="billingEmail" type="text" placeholder="example@atnorth.com" />
                </div>
            </div>
            <div class="input-group">
                <label for="phone">Phone number</label>
                <div class="input-wrapper">
                    <IntlTelInput
                        :options="{ initialCountry: 'is' }"
                        @change-number="setBillingPhoneNumber"
                        @change-validity="setBillingValidPhoneNumber"
                    />
                </div>
            </div>
        </div>
    </form>

    <Button :disabled="!validData" class="w-1/3" @click="console.log('valid data:', dataPayload)"> Submit </Button>
</template>

<script lang="ts" setup>
import { Button } from '@/components/ui/button';
// @ts-expect-error intl-tel-input ships no types for the vueWithUtils entry
import IntlTelInput from 'intl-tel-input/vueWithUtils';
import 'intl-tel-input/styles';
import { computed, ref, watch } from 'vue';

const vatNo = ref('');
const zip = ref(0);
const city = ref('');
const address = ref('');
const email = ref('');
const phoneNumber = ref('');
const validPhoneNumber = ref(false);

function setPhoneNumber(value: string) {
    phoneNumber.value = value;
}

function setValidPhoneNumber(value: boolean) {
    validPhoneNumber.value = value;
}
const website = ref('');

const billingContact = ref('');
const billingDepartment = ref('');
const billingZip = ref(0);
const billingCity = ref('');
const billingAddress = ref('');
const billingEmail = ref('');
const billingPhoneNumber = ref('');
const billingValidPhoneNumber = ref(false);

function setBillingPhoneNumber(value: string) {
    billingPhoneNumber.value = value;
}

function setBillingValidPhoneNumber(value: boolean) {
    billingValidPhoneNumber.value = value;
}

const validData = ref(false);

const dataPayload = computed(() => ({
    vatNo: vatNo.value,
    zip: Number(zip.value),
    city: city.value,
    address: address.value,
    email: email.value,
    phoneNumber: phoneNumber.value,
    validPhoneNumber: validPhoneNumber.value,
    website: website.value,
    billingContact: billingContact.value,
    billingDepartment: billingDepartment.value,
    billingZip: Number(billingZip.value),
    billingCity: billingCity.value,
    billingAddress: billingAddress.value,
    billingEmail: billingEmail.value,
    billingPhoneNumber: billingPhoneNumber.value,
    billingValidPhoneNumber: billingValidPhoneNumber.value,
}));

const validateData = () => {
    return !!(
        vatNo.value.trim() !== '' &&
        zip.value &&
        city.value.trim() !== '' &&
        address.value.trim() !== '' &&
        email.value.trim() !== '' &&
        phoneNumber.value.trim() !== '' &&
        validPhoneNumber.value &&
        website.value.trim() !== '' &&
        billingContact.value.trim() !== '' &&
        billingDepartment.value.trim() !== '' &&
        billingZip.value &&
        billingCity.value.trim() !== '' &&
        billingAddress.value.trim() !== '' &&
        billingEmail.value.trim() !== '' &&
        billingPhoneNumber.value.trim() &&
        billingValidPhoneNumber.value
    );
};

watch(dataPayload, () => {
    validData.value = validateData();
    console.table(dataPayload.value);
});
</script>

<style scoped>
@reference '@/assets/styles/default.css';

label {
    display: flex;
    align-items: center;
    margin-bottom: var(--spacing-xs);
}

.required::before {
    content: '*';
    margin-right: var(--spacing-xs);
    color: var(--color-error);
    display: flex;
    align-items: center;
}
</style>
